import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { apiRoutes } from "./apiRoutes";
import { fetchData } from "./methods";

const AvatarContext = createContext(null);

export const useAvatarRegistry = () => {
  const ctx = useContext(AvatarContext);
  if (!ctx) {
    throw new Error("useAvatarRegistry must be used within an AvatarProvider");
  }
  return ctx;
};

// Batches avatar lookups for rows that actually scroll into view, instead of
// firing one request per row or embedding avatars in the bulk persons fetch
// (both were tried and rejected — see /personsavatars on gs.rest for why).
//
// Trailing debounce, not leading: a fixed window from the *first* queued id
// splits a normal scroll (rows trickling into view a few ms apart) into many
// small batches, because most ids land just outside that window. Resetting
// the timer on every new id lets a scroll-in-progress keep extending the
// window so it collects into one request; MAX_WAIT_MS caps how long a
// nonstop scroll can keep pushing that off, and hitting BATCH_SIZE flushes
// immediately regardless of either timer.
const BATCH_SIZE = 50;
const TRAILING_DELAY_MS = 120;
const MAX_WAIT_MS = 400;

export const AvatarProvider = ({ children }) => {
  const cacheRef = useRef(new Map()); // id -> dataUrl | null (null = fetched, empty)
  const pendingRef = useRef(new Set());
  const listenersRef = useRef(new Map()); // id -> Set<callback>
  const trailingTimerRef = useRef(null);
  const maxWaitTimerRef = useRef(null);
  const observerRef = useRef(null);

  const notify = useCallback((id) => {
    const subs = listenersRef.current.get(id);
    if (subs) {
      const value = cacheRef.current.get(id) ?? null;
      subs.forEach((cb) => cb(value));
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (trailingTimerRef.current) {
      clearTimeout(trailingTimerRef.current);
      trailingTimerRef.current = null;
    }
    if (maxWaitTimerRef.current) {
      clearTimeout(maxWaitTimerRef.current);
      maxWaitTimerRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    clearTimers();
    const ids = [...pendingRef.current].slice(0, BATCH_SIZE);
    if (!ids.length) return;
    ids.forEach((id) => pendingRef.current.delete(id));

    fetchData(
      `${apiRoutes.getPersonAvatars}?ids=${ids.map(encodeURIComponent).join(",")}`,
      null,
      null,
      null,
    )
      .then((result) => {
        const map = result?.data || {};
        ids.forEach((id) => {
          cacheRef.current.set(id, map[id] || null);
          notify(id);
        });
      })
      .catch(() => {
        ids.forEach((id) => {
          cacheRef.current.set(id, null);
          notify(id);
        });
      });

    // A fast scroll can queue more than one batch's worth between renders —
    // drain the rest right away instead of waiting out another debounce.
    if (pendingRef.current.size) {
      flush();
    }
  }, [notify, clearTimers]);

  const schedule = useCallback(
    (id) => {
      pendingRef.current.add(id);

      if (pendingRef.current.size >= BATCH_SIZE) {
        flush();
        return;
      }

      if (trailingTimerRef.current) clearTimeout(trailingTimerRef.current);
      trailingTimerRef.current = setTimeout(flush, TRAILING_DELAY_MS);

      if (!maxWaitTimerRef.current) {
        maxWaitTimerRef.current = setTimeout(flush, MAX_WAIT_MS);
      }
    },
    [flush],
  );

  const getObserver = useCallback(() => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const id = entry.target.dataset.avatarId;
            observerRef.current.unobserve(entry.target);
            if (id && !cacheRef.current.has(id)) {
              schedule(id);
            }
          });
        },
        { rootMargin: "200px" },
      );
    }
    return observerRef.current;
  }, [schedule]);

  const subscribe = useCallback((id, cb) => {
    if (!listenersRef.current.has(id)) listenersRef.current.set(id, new Set());
    listenersRef.current.get(id).add(cb);
    return () => {
      const subs = listenersRef.current.get(id);
      if (subs) {
        subs.delete(cb);
        if (!subs.size) listenersRef.current.delete(id);
      }
    };
  }, []);

  const getCached = useCallback((id) => cacheRef.current.get(id), []);

  // Lets a successful add/edit push the fresh photo straight into the
  // registry instead of waiting on a reload — without this, an id that was
  // already cached (row previously scrolled into view) keeps showing the
  // pre-edit avatar indefinitely, since the cache has no TTL/expiry.
  const setCached = useCallback(
    (id, dataUrl) => {
      if (!id) return;
      cacheRef.current.set(id, dataUrl || null);
      notify(id);
    },
    [notify],
  );

  const value = useMemo(
    () => ({ getObserver, subscribe, getCached, setCached }),
    [getObserver, subscribe, getCached, setCached],
  );

  return (
    <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>
  );
};
